import { useEffect, useState } from "react";
import type { MouseEvent } from "react";
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, ListItemText, Menu, MenuItem, Stack,
  TextField,
} from "@mui/material";
import AlarmAddIcon from "@mui/icons-material/AlarmAdd";
import AlarmOnIcon from "@mui/icons-material/AlarmOn";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs, { type Dayjs } from "dayjs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BILL_REMINDERS_KEY, dismissBillReminder, fetchBillReminders, formatRemindAt, inHours, nextMondayAt, saveBillReminder,
  tomorrowAt,
} from "@/api/billReminders";

function errorMessage(error: unknown): string {
  const data = (error as { response?: { data?: { error?: string; errors?: string[] } } })?.response?.data;
  return data?.error ?? data?.errors?.join(", ") ?? (error as Error)?.message ?? "Something went wrong";
}

// Shared with the queue rows' right-click menu.
export const REMINDER_PRESETS: { label: string; at: () => Date }[] = [
  { label: "In 3 hours", at: () => inHours(3) },
  { label: "Tomorrow 8:00 AM", at: () => tomorrowAt(8) },
  { label: "Monday 8:00 AM", at: () => nextMondayAt(8) },
];

// "Remind me to revisit this bill": set, change or cancel your own reminder on
// a queue item. Shares the tray's query so both stay in step.
// autoOpenDialog: open "Pick a time" straight away (from the right-click menu);
// onAutoOpened lets the caller clear that request so it runs once.
export function RemindMeButton({ decisionId, autoOpenDialog, onAutoOpened }: {
  decisionId: number;
  autoOpenDialog?: boolean;
  onAutoOpened?: () => void;
}) {
  const qc = useQueryClient();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [when, setWhen] = useState<Dayjs | null>(null);
  const [note, setNote] = useState("");

  const list = useQuery({ queryKey: BILL_REMINDERS_KEY, queryFn: fetchBillReminders });
  const current = list.data?.reminders.find((r) => r.triage_decision_id === decisionId);

  const onDone = () => qc.invalidateQueries({ queryKey: BILL_REMINDERS_KEY });
  const save = useMutation({
    mutationFn: ({ at, note }: { at: Date; note?: string }) => saveBillReminder(decisionId, at, note),
    onSuccess: () => { setDialogOpen(false); onDone(); },
  });
  const cancel = useMutation({ mutationFn: (id: number) => dismissBillReminder(id), onSuccess: onDone });

  const close = () => setAnchor(null);
  const pick = (at: Date) => { close(); save.mutate({ at, note: current?.note ?? undefined }); };
  const openDialog = () => {
    close();
    setWhen(dayjs(current?.remind_at ?? tomorrowAt(8)));
    setNote(current?.note ?? "");
    save.reset();
    setDialogOpen(true);
  };

  useEffect(() => {
    if (!autoOpenDialog || list.isLoading) return;
    openDialog();
    onAutoOpened?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenDialog, list.isLoading]);

  return (
    <>
      <Button
        size="small" variant={current ? "contained" : "outlined"} color={current?.due ? "secondary" : "primary"}
        startIcon={current ? <AlarmOnIcon /> : <AlarmAddIcon />}
        onClick={(e: MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)}
        disabled={save.isPending || cancel.isPending}
        sx={{ textTransform: "none", whiteSpace: "nowrap" }}
      >
        {current ? `Reminder: ${formatRemindAt(current.remind_at)}` : "Remind me"}
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
        {REMINDER_PRESETS.map((p) => (
          <MenuItem key={p.label} onClick={() => pick(p.at())}>{p.label}</MenuItem>
        ))}
        <MenuItem onClick={openDialog}>
          <ListItemText>{current ? "Change time or note…" : "Pick a time, add a note…"}</ListItemText>
        </MenuItem>
        {current && <Divider />}
        {current && (
          <MenuItem onClick={() => { close(); cancel.mutate(current.id); }}>Cancel reminder</MenuItem>
        )}
      </Menu>
      {save.isError && !dialogOpen && (
        <Alert severity="error" sx={{ mt: 1 }}>{errorMessage(save.error)}</Alert>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Remind me to revisit this bill</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <DateTimePicker label="When" value={when} onChange={setWhen} disablePast />
              <TextField
                label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)}
                multiline minRows={2} placeholder="e.g. waiting on the PO receipt"
              />
              {save.isError && <Alert severity="error">{errorMessage(save.error)}</Alert>}
            </Stack>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained" disabled={!when?.isValid() || save.isPending}
            onClick={() => when && save.mutate({ at: when.toDate(), note: note.trim() })}
          >
            Save reminder
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
